import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, FileText, GitBranch, Warning, Target } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const useDebounced = (value, ms = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const debounced = useDebounced(q, 250);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!debounced.trim()) { setResults(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(debounced)}&limit=6`);
        setResults(data);
        setActiveIdx(0);
      } catch (_e) { setResults(null); }
    })();
  }, [debounced]);

  const flatResults = results ? [
    ...results.files.map((r) => ({ ...r, _kind: "file" })),
    ...results.processes.map((r) => ({ ...r, _kind: "process" })),
    ...results.anomalies.map((r) => ({ ...r, _kind: "anomaly" })),
    ...results.recommendations.map((r) => ({ ...r, _kind: "recommendation" })),
  ] : [];

  const gotoResult = (r) => {
    setOpen(false); setQ("");
    if (r._kind === "file") nav("/media");
    else if (r._kind === "process") nav("/processes");
    else if (r._kind === "anomaly") nav("/insights");
    else if (r._kind === "recommendation") nav("/reports");
  };

  const onKeyDown = (e) => {
    if (!flatResults.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatResults.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); gotoResult(flatResults[activeIdx]); }
  };

  return (
    <div className="relative flex-1 max-w-md" ref={boxRef}>
      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <Input
        ref={inputRef}
        data-testid="global-search"
        placeholder="Search files, processes, anomalies, recommendations…  (⌘K)"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => q && setOpen(true)}
        onKeyDown={onKeyDown}
        className="pl-8 pr-8 h-9 bg-[#13151A] border-[#272A35] text-sm"
      />
      {q && (
        <button onClick={() => { setQ(""); setResults(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
          <X size={12} />
        </button>
      )}

      {open && q && (
        <div data-testid="search-dropdown"
             className="absolute top-full left-0 right-0 mt-2 bg-[#0F1117] border border-[#272A35] rounded-md shadow-2xl overflow-hidden z-50 max-h-[420px] overflow-y-auto">
          {!results ? (
            <div className="p-4 text-xs text-slate-500">Searching…</div>
          ) : results.total === 0 ? (
            <div className="p-4 text-xs text-slate-500">No matches for "<span className="text-slate-300">{q}</span>"</div>
          ) : (
            <>
              <SearchSection label="Processes" icon={GitBranch} items={results.processes} color="#00E5FF"
                             render={(r) => `${r.name} · ${r.domain || "—"} · health ${r.health ?? "—"}`}
                             onClick={(r) => gotoResult({ ...r, _kind: "process" })}
                             activeIdx={activeIdx} flat={flatResults} kind="process" />
              <SearchSection label="Anomalies" icon={Warning} items={results.anomalies} color="#FF3B30"
                             render={(r) => `[${r.severity}] ${r.title}`}
                             onClick={(r) => gotoResult({ ...r, _kind: "anomaly" })}
                             activeIdx={activeIdx} flat={flatResults} kind="anomaly" />
              <SearchSection label="Recommendations" icon={Target} items={results.recommendations} color="#8B5CF6"
                             render={(r) => `${r.title} · ${r.priority} · impact ${r.impact_score ?? "—"}`}
                             onClick={(r) => gotoResult({ ...r, _kind: "recommendation" })}
                             activeIdx={activeIdx} flat={flatResults} kind="recommendation" />
              <SearchSection label="Files" icon={FileText} items={results.files} color="#FFB000"
                             render={(r) => `${r.original_filename} · ${r.kind || "file"}${(r.tags || []).length ? " · " + r.tags.join(", ") : ""}`}
                             onClick={(r) => gotoResult({ ...r, _kind: "file" })}
                             activeIdx={activeIdx} flat={flatResults} kind="file" />
              <div className="px-3 py-2 border-t border-[#272A35] text-[10px] text-slate-500 flex items-center justify-between">
                <span>{results.total} matches</span>
                <span>↑ ↓ to navigate · ↵ to open</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const SearchSection = ({ label, icon: Icon, items, color, render, onClick, activeIdx, flat, kind }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="px-3 py-2 data-label flex items-center gap-2 border-b border-[#272A35] bg-[#13151A]">
        <Icon size={12} style={{ color }} /> {label} · {items.length}
      </div>
      {items.map((r) => {
        const idx = flat.findIndex((x) => x.id === r.id && x._kind === kind);
        const active = idx === activeIdx;
        return (
          <button
            key={`${kind}-${r.id}`}
            data-testid={`search-result-${kind}-${r.id}`}
            onClick={() => onClick(r)}
            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${
              active ? "bg-[#1C1F26] text-white" : "hover:bg-[#13151A] text-slate-300"
            }`}
          >
            <span className="w-1 h-1 rounded-full shrink-0" style={{ background: color }} />
            <span className="truncate">{render(r)}</span>
          </button>
        );
      })}
    </div>
  );
};
